import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { axiosPrivate } from '@/shared/api/axios'
import { API } from '@/shared/constants/api'
import { queryKeys } from '@/shared/constants/query-keys'

export interface ICompanyLookup {
  _id: string
  id: string
  /**
   * El `cmp_` con el que v2 direcciona esta empresa. Viaja al lado del `_id` para que
   * un select pueda quedarse en este lookup y aun así resolver lo elegido a una
   * referencia v2, cruzando por clave primaria del mismo documento. Ver ADR 0027.
   *
   * Opcional porque el schema lo declara así: un registro anterior al backfill no lo
   * tiene, y quien traduce debe FALLAR en vez de mandar `undefined`.
   */
  publicId?: string | null
  name: string
  default?: boolean
}

/**
 * Lookup mínimo ({ _id, id, name, default }) para selects dependientes
 * (sociedades, agencias, clientes, negociaciones). `COMPANY_DEFAULT` expone
 * la única empresa predeterminada para preseleccionarla. El listado admin
 * vive en use-companies-admin-query.ts.
 */
export const useCompaniesLookupQuery = (dependence = true) => {
  const { data, status, refetch, isLoading } = useQuery<ICompanyLookup[]>({
    queryKey: queryKeys.companies.lookup(),
    queryFn: async () => {
      try {
        const response = await axiosPrivate.get(API.companies.lookup)
        return response.data
      } catch (e) {
        console.error(e)
        return []
      }
    },
    refetchOnWindowFocus: false,
    enabled: dependence
  })

  const COMPANY_DEFAULT = useMemo(() => data?.find((c) => c.default), [data])

  return { data, status, refetch, isLoading, COMPANY_DEFAULT }
}
